// src/features/projects/hub/peopleRolesModel.js
//
// PMO Slice C — pure helpers for the People & roles matrix. Columns are the
// project Owner scope (trackKey null) plus the seven canonical tracks.

import { TRACKS, REGION_ORDER } from "../weekly/weeklyModel.js";
import { searchDirectoryPeople } from "../../../utils/peopleDirectorySearch.js";
import { OWNERSHIP_SLOTS } from "../../../lib/projectTracks.js";

export const OWNERSHIP_COLUMNS = Object.freeze(OWNERSHIP_SLOTS.map(s => ({ key: null, id: s.key, ownershipSlot: s.key, label: s.label })));
export const OWNER_COL = OWNERSHIP_COLUMNS[2];
export const MATRIX_COLUMNS = Object.freeze([...OWNERSHIP_COLUMNS, ...TRACKS.map((t) => ({ key: t.key, id: t.key, label: t.label }))]);
export const PORTFOLIO_ROLE_LABELS = Object.freeze({ delivery_director: "Delivery Director", delivery_owner: "Delivery Owner" });

export function initials(name) {
  const parts = String(name || "").replace(/\(.*?\)/g, "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

/** Primary first, then stable by creation order the server already provides. */
export function cellPeople(participants, column) {
  const key = typeof column === "object" && column !== null ? column.key : column;
  const slot = column?.ownershipSlot || "delivery_owner";
  return (participants || []).filter((a) => (a.trackKey ?? null) === key
    && (key !== null || (a.ownershipSlot || "delivery_owner") === slot))
    .sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)));
}

export function cellHasPrimary(people) {
  return people.some((p) => p.isPrimary);
}

/** Region grouped rows; unknown region last. */
export function groupRows(projects) {
  const rank = (r) => { const i = REGION_ORDER.indexOf(r); return i >= 0 ? i : r ? REGION_ORDER.length : REGION_ORDER.length + 1; };
  const sorted = [...(projects || [])].sort((a, b) => rank(a.region) - rank(b.region) || String(a.region || "").localeCompare(String(b.region || "")) || String(a.name).localeCompare(String(b.name)) || (a.id < b.id ? -1 : 1));
  const groups = [];
  for (const p of sorted) {
    const label = p.region || "Unknown region";
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.rows.push(p); else groups.push({ label, rows: [p] });
  }
  return groups;
}

export function coverage(projects, participantsById) {
  let assignments = 0, gaps = 0;
  for (const p of projects || []) {
    const list = participantsById[p.id];
    if (!Array.isArray(list)) continue;
    assignments += list.length;
    for (const c of MATRIX_COLUMNS) if (!cellPeople(list, c).length) gaps += 1;
  }
  return { projects: (projects || []).length, assignments, gaps };
}

/** Participant write gate (UX only; server re-checks). Explicit admins only. */
export function canManageRow({ role }) {
  return role === "admin";
}

/** Directory picks: active people only, folded search over name/email/org. */
export function pickable(directory, query) {
  return searchDirectoryPeople(directory, query);
}

export function personKey(p) {
  return p?.personId || p?.id || null;
}

/** Per-person portfolio role replacement that preserves the other role. */
export function toggledRoles(current, role, on) {
  const set = new Set((current || []).filter((r) => r in PORTFOLIO_ROLE_LABELS));
  if (on) set.add(role); else set.delete(role);
  return Object.keys(PORTFOLIO_ROLE_LABELS).filter((r) => set.has(r));
}

export function rolesByPerson(roles) {
  const map = {};
  for (const r of roles || []) (map[r.personId] = map[r.personId] || []).push(r.role);
  return map;
}
